import React from 'react';

import { Lock } from 'lucide-react';

import { formatCurrency } from '../../utils/utils';

import RevenueAnalysisSummary from './RevenueAnalysisSummary';
import { CARD, CARD_TITLE } from './v9';

import type { RevenueAnalysis } from '../../types';

export const ISO_PROFIT_CARD_ID = 'iso-profit-card';
// Printed and calculated totals tie when they agree to the cent.
const TIE_TOLERANCE = 0.005;

export interface ChecklistItem {
	state: 'ok' | 'warn' | '';
	text: string;
	action?: { label: string; onClick: () => void };
}

/** v9 review checklist rows from the data we have: the printed total and the template. */
export const checklistItems = ({
	printedTotal,
	calculatedTotal,
	templateName,
	onChangeTemplate,
}: {
	printedTotal?: number | null;
	calculatedTotal?: number | null;
	templateName?: string | null;
	/** Omitted while the quote is locked (ROC-105): no "change" link then. */
	onChangeTemplate?: () => void;
}): ChecklistItem[] => {
	const items: ChecklistItem[] = [];
	if (printedTotal != null && calculatedTotal != null) {
		items.push(
			Math.abs(printedTotal - calculatedTotal) < TIE_TOLERANCE
				? {
						state: 'ok',
						text: `Printed total ties: ${formatCurrency(printedTotal)}`,
					}
				: {
						state: 'warn',
						text: `The figures read add up to ${formatCurrency(calculatedTotal)}. The statement prints ${formatCurrency(printedTotal)}.`,
					},
		);
	}
	if (templateName) {
		items.push({
			state: '',
			text: `Template: ${templateName}`,
			...(onChangeTemplate && {
				action: { label: 'change', onClick: onChangeTemplate },
			}),
		});
	}
	return items;
};

const DOT: Record<ChecklistItem['state'], string> = {
	ok: 'bg-[#a2e63c]',
	warn: 'bg-[#ff9d1c]',
	'': 'bg-gray-300',
};

interface AnalysisSideCardsProps {
	revenueAnalysis?: RevenueAnalysis;
	analysisType?: string;
	checklist: ChecklistItem[];
}

const ProfitRow: React.FC<{ label: string; value?: number | null }> = ({
	label,
	value,
}) =>
	value == null ? null : (
		<div className="flex justify-between border-t border-white/[.14] py-1.5 text-[13px] text-[#9b9ec6]">
			<span>{label}</span>
			<b className="font-medium text-white">
				{value < 0 ? `−${formatCurrency(-value)}` : formatCurrency(value)}
			</b>
		</div>
	);

/** v9 bottom row: ISO profit (restricted) and the review checklist. */
const AnalysisSideCards: React.FC<AnalysisSideCardsProps> = ({
	revenueAnalysis,
	analysisType,
	checklist,
}) => {
	if (!revenueAnalysis && checklist.length === 0) return null;
	const breakdown = revenueAnalysis?.breakdown;
	const details = breakdown?.details;
	return (
		<div className="mx-auto w-full max-w-[1440px] px-8 pb-16 pt-6">
			<div className="grid grid-cols-2 gap-6">
				{revenueAnalysis && (
					<div
						id={ISO_PROFIT_CARD_ID}
						className="rounded-lg bg-primary-500 p-5 text-white"
					>
						<div className="flex items-center gap-2 text-xs text-[#9b9ec6]">
							Revenue analysis · ISO profit
							<span className="inline-flex items-center gap-1.5 rounded-full bg-white/[.12] px-2 py-1 text-xs font-medium text-white [&_svg]:h-3 [&_svg]:w-3">
								<Lock />
								Restricted
							</span>
							<span className="ml-auto">
								<RevenueAnalysisSummary
									revenueAnalysis={revenueAnalysis}
									analysisType={analysisType}
								/>
							</span>
						</div>
						<div className="mt-2 text-[28px] font-semibold">
							{formatCurrency(revenueAnalysis.total_revenue)}{' '}
							<span className="text-sm font-normal text-primary-100">
								/ month
							</span>
						</div>
						<div className="mt-3">
							<ProfitRow
								label="Volume margin"
								value={breakdown?.volume_margin}
							/>
							<ProfitRow label="Auth margin" value={breakdown?.auth_margin} />
							<ProfitRow
								label="Monthly fee margin"
								value={breakdown?.monthly_margin}
							/>
							<ProfitRow label="Batch margin" value={breakdown?.batch_margin} />
							<ProfitRow
								label={`Buy rate cost${revenueAnalysis.risk_tier ? ` · ${revenueAnalysis.risk_tier}` : ''}`}
								value={
									details?.buy_rate_cost == null ? null : -details.buy_rate_cost
								}
							/>
							<ProfitRow
								label="Gross profit"
								value={revenueAnalysis.total_revenue}
							/>
							<ProfitRow
								label={`Agent earns · ${revenueAnalysis.agent_share_percent}%`}
								value={revenueAnalysis.agent_share}
							/>
						</div>
					</div>
				)}
				{checklist.length > 0 && (
					<div className={CARD}>
						<div className="flex items-center justify-between gap-4 px-6 pt-6">
							<div className={CARD_TITLE}>Review checklist</div>
						</div>
						<ul className="m-0 list-none p-6">
							{checklist.map((item, index) => (
								<li
									key={item.text}
									className={`flex items-start gap-3 text-sm ${index ? 'border-t border-gray-100 py-2.5' : 'pb-2.5'}`}
								>
									<span
										className={`mt-[7px] h-2 w-2 flex-none rounded-full ${DOT[item.state]}`}
									/>
									<div className="min-w-0 flex-1">{item.text}</div>
									{item.action && (
										<button
											type="button"
											onClick={item.action.onClick}
											className="ml-auto whitespace-nowrap text-xs text-gray-500 hover:text-primary-700"
										>
											{item.action.label}
										</button>
									)}
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</div>
	);
};

export default AnalysisSideCards;
